import { ILeaf, INode } from 'shared/types/tree'
import {
  makeKiddomGetRequest,
  makeKiddomPostRequest,
  makeKiddomPutRequest,
} from 'infrastructure/api'
import { useCallback } from 'react'
import {
  useWithErrorNotification,
  useWithErrorNotificationStatus,
} from 'shared/ui/GlobalDialog'
import { NodeBodyKeys } from 'shared/types/ingestion'
import { normalizeNodes } from '../tree_editing/utility'

type TreeIds = {
  tree_id: string
  version: string
  id: string
}

export function retrieveIds(nodeOrLeaf: TreeIds) {
  return {
    id: nodeOrLeaf.id,
    tree_id: nodeOrLeaf.tree_id,
    version: nodeOrLeaf.version,
  }
}

type NodeRequestData = Omit<Partial<INode>, 'leaves' | 'nodes'>

export type NodeRequestParams = NodeRequestData &
  TreeIds & {
    next_sibling_node_id?: string
    leaves?: Partial<ILeaf>[]
    nodes?: NodeRequestData[]
  }
export type LeafRequestParams = Partial<ILeaf> & TreeIds
export type LeafCreateParams = { leaves: Partial<ILeaf>[] } & TreeIds

function updateNodeRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/${id}`
}

function updateLeafRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/leaf/${id}`
}

function createLeafRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/node/${id}/leaf`
}

function appendRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/append/${tree_id}:${version}/${id}`
}

function appendBulkRoute({ tree_id, version, id }: TreeIds) {
  return `/v1/tree/append/${tree_id}:${version}/${id}/bulk`
}

export function updateTreeNode({
  tree_id,
  version,
  id,
  ...rest
}: NodeRequestParams) {
  return makeKiddomPutRequest({
    kiddomPath: updateNodeRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

export function useUpdateTreeNode({
  showSuccess,
}: { showSuccess?: boolean } = {}) {
  const request = useWithErrorNotificationStatus(
    {
      errorTitle: 'Error Updating Node',
      successMessage: showSuccess ? 'Node has been updated' : undefined,
    },
    updateTreeNode,
  )
  return useCallback(
    (p: NodeRequestParams) => {
      return request(p)
    },
    [request],
  )
}

export function createLeaf({
  tree_id,
  version,
  id,
  ...rest
}: LeafCreateParams) {
  return makeKiddomPostRequest({
    kiddomPath: createLeafRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

export function updateLeafData({
  tree_id,
  version,
  id,
  ...rest
}: LeafRequestParams) {
  return makeKiddomPutRequest({
    kiddomPath: updateLeafRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

export function useCreateLeaf() {
  const request = useWithErrorNotification(
    { errorTitle: 'Error Creating Leaf' },
    createLeaf,
  )
  return useCallback(
    (params: LeafCreateParams) => {
      return request(params)
    },
    [request],
  )
}

export function useUpdateLeafData() {
  const request = useWithErrorNotification(
    { errorTitle: 'Error Updating Leaf' },
    updateLeafData,
  )
  return useCallback(
    (params: LeafRequestParams) => {
      return request(params)
    },
    [request],
  )
}

export async function appendNodeToTree({
  tree_id,
  version,
  id,
  ...rest
}: NodeRequestParams) {
  const doBulkAppend = 'trees' in rest
  const routeFunc = doBulkAppend ? appendBulkRoute : appendRoute

  const { result, error } = await makeKiddomPutRequest<INode>({
    kiddomPath: routeFunc({ tree_id, version, id }),
    requestBody: {
      ...rest,
    },
  })
  return {
    error,
    result: result && normalizeNodes(result),
  }
}

export function useUpdateStudentFacing() {
  const request = useUpdateTreeNode()
  return useCallback(
    async (studentFacing: boolean, ...nodes: INode[]) => {
      return Promise.all(
        nodes.map(async (node) => {
          const params = {
            ...retrieveIds(node),
            body: {
              ...node.body,
              [NodeBodyKeys.StudentFacing]: studentFacing,
            },
          }
          const succeeded = await request(params)
          if (succeeded) {
            if (!node.body) {
              node.body = {}
            }
            node.body[NodeBodyKeys.StudentFacing] = studentFacing
          }
        }),
      )
    },
    [request],
  )
}

export async function getTree({
  treeId,
  versionId,
  depth = -1,
}: {
  treeId: string
  versionId: string
  depth?: number
}) {
  let kiddomPath = `/api/tree/${treeId}:${versionId}`
  if (depth !== -1) {
    kiddomPath += `?depth=${depth}`
  }

  const { result, error } = await makeKiddomGetRequest<INode>({ kiddomPath })
  result && normalizeNodes(result)
  return { result, error }
}

export function useGetTree() {
  const treeGetter = useWithErrorNotification(
    { errorTitle: 'Error Getting Tree' },
    getTree,
  )
  return useCallback(
    (params: { treeId: string; versionId: string; depth?: number }) => {
      return treeGetter(params)
    },
    [treeGetter],
  )
}
